import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {ShieldCheck, AlertTriangle, CheckCircle2, XCircle, ChevronDown, ChevronUp, Plus, Trash2, Save, Lock, RefreshCw, ArrowLeft, ClipboardList, Minus, } from 'lucide-react'
import Layout from '@/components/Layout'
import { useAuth } from '@/hooks/useAuth'
import { useAllProjects } from '@/hooks/useProjects'
import { lumi } from '@/lib/lumi'
import { isAdmin } from '@/lib/adminConfig'
import type {
  ProjectProfile, AuditResult, AuditStatus, AuditVerdict,
  Vulnerability, VulnSeverity, ChecklistOverride,
} from '@/lib/types'
import { SEVERITY_META, AUDIT_CHECKLIST } from '@/lib/types'

const C = {
  turq:       '#1ad6c4',
  turqDim:    'rgba(26,214,196,0.10)',
  turqBright: '#3ce8d6',
  borderTurq: 'rgba(26,214,196,0.28)',
  border:     'rgba(255,255,255,0.08)',
  borderMed:  'rgba(255,255,255,0.14)',
  surface:    '#080c0f',
  surfaceAlt: '#0d1117',
  text:       '#9aa7b4',
  textMuted:  '#5a6672',
  error:      '#f87171',
  errorDim:   'rgba(248,113,113,0.12)',
  green:      '#34d399',
  greenDim:   'rgba(52,211,153,0.12)',
  neutral:    'rgba(255,255,255,0.06)',
}

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.04)',
  border: `1px solid ${C.borderMed}`,
  color: '#ffffff',
  width: '100%',
  borderRadius: 8,
  padding: '10px 12px',
  fontSize: 13,
  outline: 'none',
}

const EMPTY_AUDIT: AuditResult = {
  status: 'pending',
  verdict: null,
  score: null,
  vulnerabilities: [],
  auditedAt: null,
  summary: '',
  checklistOverrides: {},
}

function newVuln(): Vulnerability {
  return {
    id: `v-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    title: '',
    severity: 'medium',
    description: '',
    resolved: false,
  }
}

// ── 3-state checklist toggle ───────────────────────────────────────────────
// state: null = not reviewed, 'pass' = green/right, 'fail' = red/left
function ChecklistToggle({
  item,
  override,
  onChange,
}: {
  item: { id: string; label: string; description: string }
  override: ChecklistOverride
  onChange: (next: ChecklistOverride) => void
}) {
  const state = override.state

  // Cycle: null → pass → fail → null
  function cycleState() {
    if (state === null) onChange({ state: 'pass' })
    else if (state === 'pass') onChange({ state: 'fail', reason: override.reason ?? '' })
    else onChange({ state: null })
  }

  function setPass() { onChange({ state: 'pass' }) }
  function setFail() { onChange({ state: 'fail', reason: override.reason ?? '' }) }

  const borderColor =
    state === 'pass' ? C.green :
    state === 'fail' ? C.error :
    C.borderMed

  const bgColor =
    state === 'pass' ? C.greenDim :
    state === 'fail' ? C.errorDim :
    'rgba(255,255,255,0.02)'

  return (
    <div
      className="rounded-xl overflow-hidden transition-all"
      style={{
        border: `1px solid ${borderColor}`,
        background: bgColor,
        transition: 'border-color 0.2s, background 0.2s',
      }}
    >
      <div className="flex items-center gap-3 px-4 py-3">
        {/* Toggle track */}
        <div
          className="relative flex-shrink-0 flex items-center"
          style={{
            width: 64,
            height: 28,
            borderRadius: 6,
            background: '#0d1117',
            border: `1px solid ${C.borderMed}`,
            cursor: 'pointer',
          }}
          onClick={cycleState}
          title="Click to cycle: Not set → Pass → Fail"
        >
          {/* Left side: fail (red square) */}
          <button
            type="button"
            onClick={e => { e.stopPropagation(); setFail() }}
            className="absolute left-1 flex items-center justify-center transition-all"
            style={{
              width: 22,
              height: 20,
              borderRadius: 4,
              background: state === 'fail' ? C.error : 'transparent',
              border: state === 'fail' ? 'none' : `1px solid ${C.textMuted}`,
              opacity: state === 'pass' ? 0.3 : 1,
            }}
            title="Mark as fail"
          >
            <XCircle
              style={{
                width: 12,
                height: 12,
                color: state === 'fail' ? '#000' : C.error,
              }}
            />
          </button>

          {/* Center: neutral dash */}
          {state === null && (
            <div
              className="absolute"
              style={{
                left: '50%',
                transform: 'translateX(-50%)',
                width: 16,
                height: 2,
                borderRadius: 2,
                background: C.textMuted,
              }}
            />
          )}

          {/* Right side: pass (green square) */}
          <button
            type="button"
            onClick={e => { e.stopPropagation(); setPass() }}
            className="absolute right-1 flex items-center justify-center transition-all"
            style={{
              width: 22,
              height: 20,
              borderRadius: 4,
              background: state === 'pass' ? C.green : 'transparent',
              border: state === 'pass' ? 'none' : `1px solid ${C.textMuted}`,
              opacity: state === 'fail' ? 0.3 : 1,
            }}
            title="Mark as pass"
          >
            <CheckCircle2
              style={{
                width: 12,
                height: 12,
                color: state === 'pass' ? '#000' : C.green,
              }}
            />
          </button>
        </div>

        {/* Label + state badge */}
        <div className="flex-1 min-w-0">
          <p
            className="text-sm font-medium leading-tight"
            style={{ color: state === 'pass' ? '#fff' : state === 'fail' ? C.error : C.text }}
          >
            {item.label}
          </p>
          <p className="text-xs mt-0.5 truncate" style={{ color: C.textMuted }}>
            {item.description}
          </p>
        </div>

        {/* State badge */}
        <span
          className="text-xs px-2 py-0.5 rounded-full flex-shrink-0 font-medium"
          style={{
            background: state === 'pass' ? C.greenDim : state === 'fail' ? C.errorDim : C.neutral,
            color: state === 'pass' ? C.green : state === 'fail' ? C.error : C.textMuted,
            border: `1px solid ${
              state === 'pass' ? 'rgba(52,211,153,0.3)' :
              state === 'fail' ? 'rgba(248,113,113,0.3)' :
              C.borderMed
            }`,
          }}
        >
          {state === 'pass' ? 'Pass' : state === 'fail' ? 'Fail' : '—'}
        </span>
      </div>

      {/* Reason input — only shown when failed */}
      {state === 'fail' && (
        <div
          className="px-4 pb-3"
          style={{ borderTop: `1px solid rgba(248,113,113,0.18)` }}
        >
          <label
            className="block text-xs font-medium mb-1.5 mt-2"
            style={{ color: C.error }}
          >
            Reason for failure *
          </label>
          <textarea
            style={{
              ...inputStyle,
              resize: 'vertical',
              minHeight: 56,
              border: `1px solid rgba(248,113,113,0.35)`,
              fontSize: 12,
            }}
            value={override.reason ?? ''}
            onChange={e => onChange({ state: 'fail', reason: e.target.value })}
            placeholder="Describe why this check failed (shown on the public audit page)."
            rows={2}
          />
        </div>
      )}
    </div>
  )
}

// ── Vulnerability row editor ──────────────────────────────────────────────────
function VulnRow({
  vuln,
  index,
  onChange,
  onRemove,
}: {
  vuln: Vulnerability
  index: number
  onChange: (updated: Vulnerability) => void
  onRemove: () => void
}) {
  const [open, setOpen] = useState(true)
  const meta = SEVERITY_META[vuln.severity]

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ border: `1px solid ${C.borderMed}`, background: C.surfaceAlt }}
    >
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
        onClick={() => setOpen(v => !v)}
        style={{ borderLeft: `3px solid ${meta.color}` }}
      >
        <span
          className="text-xs font-semibold px-2 py-0.5 rounded-full"
          style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }}
        >
          {meta.label}
        </span>
        <span className="flex-1 text-sm text-white truncate">
          {vuln.title || `Finding #${index + 1}`}
        </span>
        <span
          className="text-xs px-2 py-0.5 rounded-full"
          style={{
            background: vuln.resolved ? C.greenDim : C.errorDim,
            color: vuln.resolved ? C.green : C.error,
          }}
        >
          {vuln.resolved ? 'Resolved' : 'Unresolved'}
        </span>
        {open
          ? <ChevronUp className="w-4 h-4" style={{ color: C.textMuted }} />
          : <ChevronDown className="w-4 h-4" style={{ color: C.textMuted }} />
        }
      </div>

      {open && (
        <div className="px-4 pb-4 pt-2 flex flex-col gap-3" style={{ borderTop: `1px solid ${C.border}` }}>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium" style={{ color: C.text }}>Title</label>
            <input
              style={inputStyle}
              value={vuln.title}
              onChange={e => onChange({ ...vuln, title: e.target.value })}
              placeholder="e.g. Reentrancy in withdraw()"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium" style={{ color: C.text }}>Severity</label>
              <select
                style={{ ...inputStyle }}
                value={vuln.severity}
                onChange={e => onChange({ ...vuln, severity: e.target.value as VulnSeverity })}
              >
                {(['critical', 'high', 'medium', 'low', 'info'] as VulnSeverity[]).map(s => (
                  <option key={s} value={s}>{SEVERITY_META[s].label}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium" style={{ color: C.text }}>Status</label>
              <select
                style={{ ...inputStyle }}
                value={vuln.resolved ? 'resolved' : 'unresolved'}
                onChange={e => onChange({ ...vuln, resolved: e.target.value === 'resolved' })}
              >
                <option value="unresolved">Unresolved</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium" style={{ color: C.text }}>Description</label>
            <textarea
              style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
              value={vuln.description}
              onChange={e => onChange({ ...vuln, description: e.target.value })}
              placeholder="Explain the vulnerability in 1–2 sentences."
              rows={3}
            />
          </div>
          <button
            type="button"
            onClick={onRemove}
            className="flex items-center gap-1.5 text-xs w-fit transition-colors hover:opacity-80"
            style={{ color: C.error }}
          >
            <Trash2 className="w-3.5 h-3.5" />
            Remove finding
          </button>
        </div>
      )}
    </div>
  )
}

// ── Main Admin Page ───────────────────────────────────────────────────────────
export default function AdminPage() {
  const { user, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const { projects, loading: projectsLoading, refetch } = useAllProjects()

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [audit, setAudit]           = useState<AuditResult>(EMPTY_AUDIT)
  const [saving, setSaving]         = useState(false)
  const [saveMsg, setSaveMsg]       = useState('')
  const [saveErr, setSaveErr]       = useState('')

  // Auth + admin guard
  useEffect(() => {
    if (!isAuthenticated) { navigate('/auth', { replace: true }); return }
    if (isAuthenticated && !isAdmin(user)) { navigate('/', { replace: true }) }
  }, [isAuthenticated, user, navigate])

  const selectedProject: ProjectProfile | undefined =
    projects.find(p => p._id === selectedId)

  // Seed editor when project selected
  useEffect(() => {
    if (!selectedProject) { setAudit(EMPTY_AUDIT); return }
    const base = selectedProject.audit ?? EMPTY_AUDIT
    // Ensure checklistOverrides exists, seed nulls for any missing items
    const overrides: Record<string, ChecklistOverride> = {}
    AUDIT_CHECKLIST.forEach(item => {
      overrides[item.id] = base.checklistOverrides?.[item.id] ?? { state: null }
    })
    setAudit({ ...base, checklistOverrides: overrides })
    setSaveMsg('')
    setSaveErr('')
  }, [selectedProject])

  // ── Checklist override helpers ──
  const updateOverride = useCallback((id: string, next: ChecklistOverride) => {
    setAudit(a => ({
      ...a,
      checklistOverrides: { ...(a.checklistOverrides ?? {}), [id]: next },
    }))
  }, [])

  // ── Vuln helpers ──
  const addVuln = useCallback(() => {
    setAudit(a => ({ ...a, vulnerabilities: [...a.vulnerabilities, newVuln()] }))
  }, [])

  const updateVuln = useCallback((index: number, updated: Vulnerability) => {
    setAudit(a => {
      const vulns = [...a.vulnerabilities]
      vulns[index] = updated
      return { ...a, vulnerabilities: vulns }
    })
  }, [])

  const removeVuln = useCallback((index: number) => {
    setAudit(a => ({ ...a, vulnerabilities: a.vulnerabilities.filter((_, i) => i !== index) }))
  }, [])

  // ── Save ──
  async function handleSave() {
    if (!selectedProject) return
    if (!selectedProject._id) {
      setSaveErr('Cannot save: project has no ID. Try refreshing the page.')
      return
    }
    setSaving(true)
    setSaveMsg('')
    setSaveErr('')
    try {
      const now = new Date().toISOString()
      const auditPayload: AuditResult = {
        ...audit,
        auditedAt: audit.status === 'completed' ? (audit.auditedAt || now) : audit.auditedAt,
      }
      const projectId = String(selectedProject._id)
      await lumi.entities.project_profiles.update(projectId, {
        audit: auditPayload,
        updatedAt: now,
      })
      setSaveMsg('Audit results saved successfully.')
      await refetch()
    } catch (err: unknown) {
      const msg = (err as Error)?.message || String(err) || 'Save failed. Please try again.'
      console.error('[Admin] Save failed:', msg, err)
      setSaveErr(msg)
    } finally {
      setSaving(false)
    }
  }

  // ── Access guard render ──
  if (!isAuthenticated || !isAdmin(user)) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 px-5">
          <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: C.errorDim }}>
            <Lock className="w-7 h-7" style={{ color: C.error }} />
          </div>
          <p className="text-white font-semibold text-lg">Access Restricted</p>
          <p className="text-sm text-center max-w-xs" style={{ color: C.textMuted }}>
            This page is only accessible to Crypto Audit Service administrators.
          </p>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all hover:bg-white/5"
            style={{ color: C.turq, border: `1px solid ${C.borderTurq}` }}
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </button>
        </div>
      </Layout>
    )
  }

  // Checklist pass count from overrides
  const overrides = audit.checklistOverrides ?? {}
  const passCount  = AUDIT_CHECKLIST.filter(i => overrides[i.id]?.state === 'pass').length
  const failCount  = AUDIT_CHECKLIST.filter(i => overrides[i.id]?.state === 'fail').length
  const unsetCount = AUDIT_CHECKLIST.length - passCount - failCount

  return (
    <Layout>
      <div className="max-w-[1100px] mx-auto px-5 py-12">

        {/* ── Page header ── */}
        <div className="flex items-start justify-between gap-4 mb-10">
          <div>
            <div className="flex items-center gap-2.5 mb-2">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ background: C.turqDim, border: `1px solid ${C.borderTurq}` }}
              >
                <ClipboardList className="w-5 h-5" style={{ color: C.turq }} />
              </div>
              <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
            </div>
            <p className="text-sm" style={{ color: C.textMuted }}>
              Manage and publish audit results for any project in the registry.
            </p>
          </div>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm transition-all hover:bg-white/5"
            style={{ color: C.text, border: `1px solid ${C.borderMed}` }}
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>

        <div className="grid lg:grid-cols-[320px,1fr] gap-8 items-start">

          {/* ── Left: Project selector ── */}
          <div
            className="rounded-2xl overflow-hidden sticky top-24"
            style={{ background: C.surface, border: `1px solid ${C.border}` }}
          >
            <div className="px-5 py-4 border-b" style={{ borderColor: C.border }}>
              <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: C.turq }}>
                Projects
              </p>
              <p className="text-xs mt-0.5" style={{ color: C.textMuted }}>
                {projects.length} in registry
              </p>
            </div>

            {projectsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 rounded-full border-2 animate-spin" style={{ borderColor: `${C.turq} transparent transparent transparent` }} />
              </div>
            ) : projects.length === 0 ? (
              <p className="px-5 py-8 text-sm text-center" style={{ color: C.textMuted }}>No projects found.</p>
            ) : (
              <div className="divide-y" style={{ '--tw-divide-opacity': 1, borderColor: C.border } as React.CSSProperties}>
                {projects.map(p => {
                  const isSelected = p._id === selectedId
                  const hasAudit = !!p.audit
                  return (
                    <button
                      key={p._id}
                      onClick={() => setSelectedId(p._id)}
                      className="w-full flex items-center gap-3 px-5 py-4 text-left transition-all hover:bg-white/4"
                      style={{
                        background: isSelected ? C.turqDim : 'transparent',
                        borderLeft: isSelected ? `3px solid ${C.turq}` : '3px solid transparent',
                      }}
                    >
                      {p.logoUrl ? (
                        <img src={p.logoUrl} alt={p.name} className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                          style={{ background: C.turqDim, color: C.turq }}
                        >
                          {p.symbol.slice(0, 2)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{p.name}</p>
                        <p className="text-xs truncate" style={{ color: C.textMuted }}>${p.symbol}</p>
                      </div>
                      <div>
                        {hasAudit && p.audit?.status === 'completed' ? (
                          p.audit?.verdict === 'passed'
                            ? <CheckCircle2 className="w-4 h-4" style={{ color: C.green }} />
                            : <XCircle className="w-4 h-4" style={{ color: C.error }} />
                        ) : hasAudit ? (
                          <div className="w-2 h-2 rounded-full" style={{ background: C.turq }} />
                        ) : (
                          <div className="w-2 h-2 rounded-full" style={{ background: C.borderMed }} />
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* ── Right: Audit editor ── */}
          {!selectedProject ? (
            <div
              className="rounded-2xl flex flex-col items-center justify-center py-24 gap-4"
              style={{ border: `1px dashed ${C.border}` }}
            >
              <ShieldCheck className="w-10 h-10" style={{ color: C.borderMed }} />
              <p className="text-sm font-medium" style={{ color: C.textMuted }}>Select a project to edit its audit results</p>
            </div>
          ) : (
            <div className="flex flex-col gap-6">

              {/* Project identity bar */}
              <div
                className="rounded-2xl p-5 flex items-center gap-4"
                style={{ background: C.surface, border: `1px solid ${C.border}` }}
              >
                {selectedProject.logoUrl ? (
                  <img src={selectedProject.logoUrl} alt={selectedProject.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                    style={{ background: C.turqDim, color: C.turq }}
                  >
                    {selectedProject.symbol.slice(0, 2)}
                  </div>
                )}
                <div>
                  <p className="text-lg font-semibold text-white">{selectedProject.name}</p>
                  <p className="text-sm" style={{ color: C.textMuted }}>
                    ${selectedProject.symbol} · {selectedProject.chain.charAt(0).toUpperCase() + selectedProject.chain.slice(1)}
                  </p>
                  <p className="text-xs font-mono mt-0.5" style={{ color: C.textMuted }}>
                    {selectedProject.contractAddress}
                  </p>
                </div>
              </div>

              {/* ── Audit Status & Verdict ── */}
              <section
                className="rounded-2xl p-6"
                style={{ background: C.surface, border: `1px solid ${C.border}` }}
              >
                <h2 className="text-sm font-semibold text-white mb-5 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" style={{ color: C.turq }} />
                  Audit Status & Outcome
                </h2>

                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-medium" style={{ color: C.text }}>Audit Status</label>
                    <select
                      style={inputStyle}
                      value={audit.status}
                      onChange={e => setAudit(a => ({ ...a, status: e.target.value as AuditStatus }))}
                    >
                      <option value="pending">Pending</option>
                      <option value="in_review">In Review</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-medium" style={{ color: C.text }}>Verdict</label>
                    <select
                      style={inputStyle}
                      value={audit.verdict ?? ''}
                      onChange={e => setAudit(a => ({
                        ...a,
                        verdict: (e.target.value as AuditVerdict) || null,
                      }))}
                    >
                      <option value="">— No verdict yet —</option>
                      <option value="passed">Passed</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-medium" style={{ color: C.text }}>Security Score (1–100)</label>
                    <input
                      type="number"
                      min={1}
                      max={100}
                      style={inputStyle}
                      value={audit.score ?? ''}
                      onChange={e => setAudit(a => ({
                        ...a,
                        score: e.target.value === '' ? null : Math.min(100, Math.max(1, Number(e.target.value))),
                      }))}
                      placeholder="e.g. 87"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-medium" style={{ color: C.text }}>Audit Date</label>
                    <input
                      type="date"
                      style={inputStyle}
                      value={audit.auditedAt ? audit.auditedAt.slice(0, 10) : ''}
                      onChange={e => setAudit(a => ({
                        ...a,
                        auditedAt: e.target.value ? new Date(e.target.value).toISOString() : null,
                      }))}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-medium" style={{ color: C.text }}>Audit Summary</label>
                  <textarea
                    style={{ ...inputStyle, resize: 'vertical', minHeight: 88 }}
                    value={audit.summary}
                    onChange={e => setAudit(a => ({ ...a, summary: e.target.value }))}
                    placeholder="1–2 sentence overview of the audit result shown on the project page."
                    rows={3}
                  />
                </div>
              </section>

              {/* ── Security Checklist ── */}
              <section
                className="rounded-2xl p-6"
                style={{ background: C.surface, border: `1px solid ${C.border}` }}
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                    <ClipboardList className="w-4 h-4" style={{ color: C.turq }} />
                    Security Checklist
                  </h2>
                  {/* Summary chips */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span
                      className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{ background: C.greenDim, color: C.green }}
                    >
                      {passCount} pass
                    </span>
                    <span
                      className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{ background: C.errorDim, color: C.error }}
                    >
                      {failCount} fail
                    </span>
                    {unsetCount > 0 && (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full font-medium"
                        style={{ background: C.neutral, color: C.textMuted }}
                      >
                        {unsetCount} not set
                      </span>
                    )}
                  </div>
                </div>

                <p className="text-xs mb-5" style={{ color: C.textMuted }}>
                  Manually set each item to Pass or Fail. Use the left square for fail (add a reason) and the right square for pass.
                </p>

                {/* Legend */}
                <div className="flex items-center gap-5 mb-5 px-1">
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-4 rounded" style={{ background: C.error }} />
                    <span className="text-xs" style={{ color: C.textMuted }}>Fail (left)</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-1.5 rounded" style={{ background: C.textMuted }} />
                    <span className="text-xs" style={{ color: C.textMuted }}>Not set</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-4 rounded" style={{ background: C.green }} />
                    <span className="text-xs" style={{ color: C.textMuted }}>Pass (right)</span>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  {AUDIT_CHECKLIST.map(item => (
                    <ChecklistToggle
                      key={item.id}
                      item={item}
                      override={overrides[item.id] ?? { state: null }}
                      onChange={next => updateOverride(item.id, next)}
                    />
                  ))}
                </div>
              </section>

              {/* ── Vulnerabilities ── */}
              <section
                className="rounded-2xl p-6"
                style={{ background: C.surface, border: `1px solid ${C.border}` }}
              >
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" style={{ color: C.turq }} />
                    Vulnerabilities
                    <span
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{ background: C.turqDim, color: C.turq }}
                    >
                      {audit.vulnerabilities.length}
                    </span>
                  </h2>
                  <button
                    type="button"
                    onClick={addVuln}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-medium transition-all hover:brightness-110"
                    style={{ background: C.turqDim, color: C.turq, border: `1px solid ${C.borderTurq}` }}
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add Finding
                  </button>
                </div>

                {audit.vulnerabilities.length === 0 ? (
                  <div
                    className="rounded-xl flex flex-col items-center justify-center py-10 gap-3"
                    style={{ border: `1px dashed ${C.border}` }}
                  >
                    <CheckCircle2 className="w-8 h-8" style={{ color: C.green }} />
                    <p className="text-sm font-medium text-white">No vulnerabilities added</p>
                    <p className="text-xs text-center" style={{ color: C.textMuted }}>
                      Add findings to document specific issues found during the audit.
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    {audit.vulnerabilities.map((v, i) => (
                      <VulnRow
                        key={v.id}
                        vuln={v}
                        index={i}
                        onChange={updated => updateVuln(i, updated)}
                        onRemove={() => removeVuln(i)}
                      />
                    ))}
                  </div>
                )}
              </section>

              {/* ── Save bar ── */}
              <div
                className="rounded-2xl px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                style={{ background: C.surfaceAlt, border: `1px solid ${C.border}` }}
              >
                <div className="flex flex-col gap-1">
                  {saveMsg && (
                    <p className="text-sm flex items-center gap-1.5" style={{ color: C.green }}>
                      <CheckCircle2 className="w-4 h-4" />
                      {saveMsg}
                    </p>
                  )}
                  {saveErr && (
                    <p className="text-sm flex items-center gap-1.5" style={{ color: C.error }}>
                      <XCircle className="w-4 h-4" />
                      {saveErr}
                    </p>
                  )}
                  {!saveMsg && !saveErr && (
                    <p className="text-xs" style={{ color: C.textMuted }}>
                      Changes are published immediately to the public project page.
                    </p>
                  )}
                </div>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: C.turq, color: '#000000', minWidth: 140 }}
                >
                  {saving
                    ? <><RefreshCw className="w-4 h-4 animate-spin" /> Saving…</>
                    : <><Save className="w-4 h-4" /> Save Audit Results</>
                  }
                </button>
              </div>

            </div>
          )}
        </div>
      </div>
    </Layout>
  )
}
